// Pure planning for the windowed top-N spellbook pick primitive:
//   • Black Mass. Search the top seven spells, draw up to three
//     DIFFERENT (different-named) Evil minions from among them, then
//     put the rest of those seven at the bottom of the spellbook.
//
// No runtime dependencies. The caller resolves the spellbook pile, opens
// the dialog on the WINDOW (top-N only. Never the whole library), and
// does the actual take-to-hand + bottom-move; these functions only
// decide which window cards are eligible, enforce the distinct-name cap,
// and order the leftover window for the bottom move.
//
// "Top" of a pile is the LAST element (mirrors drawOp.bottomCardId). So
// the top-N window is the final N entries, and the bottom is index 0
// (unshift). `getFullCard` resolves the catalog row so type / subtype
// fields absent on a bare spellbook instance still gate correctly.

import { matchesFilter } from '../../cardFilter';

/**
 * The top-N window of a pile: the last `n` cards, in pile order (top is
 * the last element). Returns a fresh array slice; the dialog renders
 * these and only these, so the rest of the spellbook is never shown.
 */
export function topWindow(cards, n) {
  if (!Array.isArray(cards) || n <= 0) return [];
  return cards.slice(Math.max(0, cards.length - n));
}

/**
 * Which window cards an effect may draw. Honors the op's `filter` (Black
 * Mass: { type: 'Minion', subtype: 'Evil' }) via the canonical
 * matchesFilter. A bare spellbook instance carries no type/subtype, so
 * we match against the catalog row merged onto the instance (keeping the
 * instance `id` for selection). `ctx.getFullCard` resolves the row.
 */
export function eligibleWindowCards(windowCards, filter = null, ctx = {}) {
  if (!filter || Object.keys(filter).length === 0) return [...windowCards];
  const getFullCard = ctx.getFullCard;
  return windowCards.filter((card) => {
    const full = getFullCard && card?.cardId ? getFullCard(card.cardId) : null;
    const view = full ? { ...full, ...card } : card;
    return matchesFilter(view, filter, ctx);
  });
}

/**
 * Display name of a window card for the distinct-name gate. Reads the
 * live instance name first, then the catalog row via getFullCard.
 */
export function cardDisplayName(card, getFullCard = null) {
  return card?.name
    ?? (getFullCard && card?.cardId ? getFullCard(card.cardId)?.name : null)
    ?? '';
}

/**
 * Would adding `candidate` to the cards already chosen (`chosenIds`)
 * violate the "different" constraint? True when another already-chosen
 * card shares `candidate`'s name. `windowCards` resolves ids → cards.
 */
export function wouldDuplicateName(windowCards, chosenIds, candidate, getFullCard = null) {
  const candidateName = cardDisplayName(candidate, getFullCard).toLowerCase();
  if (!candidateName) return false;
  for (const id of chosenIds) {
    if (id === candidate.id) continue;
    const other = windowCards.find((c) => c.id === id);
    if (other && cardDisplayName(other, getFullCard).toLowerCase() === candidateName) {
      return true;
    }
  }
  return false;
}

/**
 * Partition the window into the picked cards (to draw) and the leftover
 * (to send to the bottom, in pile/top order). `pickedIds` are the cards
 * the player chose; everything else in the window is leftover. Returns
 * `{ picked: [card...], rest: [card...] }`, both preserving window order.
 */
export function partitionWindow(windowCards, pickedIds) {
  const pickedSet = new Set(pickedIds);
  const picked = [];
  const rest = [];
  for (const card of windowCards) {
    (pickedSet.has(card.id) ? picked : rest).push(card);
  }
  return { picked, rest };
}
